import type { Reading } from "@bindery/shared/reader/cdn";
import {
  enrollmentAt,
  enrollmentKey,
  type EnrollmentMark,
  type KnownEnrollment,
} from "@/storage/enrollment";
import { readerRange, type ReaderDb } from "@/storage/db";

// A reading is tens of kilobytes and a classroom device is shared by many
// readers, so each keeps only its most recently served copies.
export const PERSONAL_READINGS_KEPT_PER_READER = 50;

export interface ServedReading {
  reading: Reading;
  bridgeSetVersion?: string;
  /** The route's served-at, by Convex's clock; not the row's `servedAt`. */
  routeServedAt?: number;
}

export type { KnownEnrollment };

export interface PersonalReadingStore {
  cached: (
    readerId: string,
    readingId: string,
  ) => Promise<ServedReading | undefined>;
  /** Keeps the served copy and marks the reader enrolled. */
  store: (
    readerId: string,
    readingId: string,
    served: ServedReading,
  ) => Promise<void>;
  /** Marks the reader not enrolled and drops every copy served to them. */
  notEnrolled: (readerId: string) => Promise<void>;
  enrollment: (readerId: string) => Promise<KnownEnrollment>;
}

export function createPersonalReadingStore({
  db,
  now = Date.now,
}: {
  db: Pick<ReaderDb, "personalReadings" | "meta" | "transaction">;
  now?: () => number;
}): PersonalReadingStore {
  const ofReader = (readerId: string) =>
    db.personalReadings
      .where("[readerId+servedAt]")
      .between(...readerRange(readerId));

  const mark = (readerId: string, enrolled: boolean) =>
    db.meta.put({
      key: enrollmentKey(readerId),
      value: { enrolled, heardAt: now() } satisfies EnrollmentMark,
    });

  return {
    async cached(readerId, readingId) {
      const row = await db.personalReadings.get([readerId, readingId]);
      if (row === undefined) return undefined;
      return {
        reading: row.reading,
        ...(row.bridgeSetVersion !== undefined && {
          bridgeSetVersion: row.bridgeSetVersion,
        }),
        ...(row.routeServedAt !== undefined && {
          routeServedAt: row.routeServedAt,
        }),
      };
    },

    async store(
      readerId,
      readingId,
      { reading, bridgeSetVersion, routeServedAt },
    ) {
      await db.transaction("rw", [db.personalReadings, db.meta], async () => {
        await db.personalReadings.put({
          readerId,
          readingId,
          reading,
          ...(bridgeSetVersion !== undefined && { bridgeSetVersion }),
          ...(routeServedAt !== undefined && { routeServedAt }),
          servedAt: now(),
        });
        const kept = await ofReader(readerId).count();
        const excess = kept - PERSONAL_READINGS_KEPT_PER_READER;
        if (excess > 0) {
          const oldest = await ofReader(readerId).limit(excess).primaryKeys();
          await db.personalReadings.bulkDelete(oldest);
        }
        await mark(readerId, true);
      });
    },

    async notEnrolled(readerId) {
      await db.transaction("rw", [db.personalReadings, db.meta], async () => {
        await ofReader(readerId).delete();
        await mark(readerId, false);
      });
    },

    async enrollment(readerId) {
      const row = await db.meta.get(enrollmentKey(readerId));
      return enrollmentAt(row?.value, now());
    },
  };
}
